import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { Badge } from '../ui/Page';
import {
  BANT_ANSWERS,
  BANT_CRITERIA,
  updateRecord,
  type BantAnswer,
  type BantCriterion,
  type Qualification,
} from './detail-api';

/**
 * BANT: four questions instead of a feeling.
 *
 * Budget, authority, need, timing. The framework every large analyst still teaches, because it turns
 * "the rep liked them" into four things two people can disagree about productively — and into
 * something a report can group by when a quarter goes badly.
 *
 * `UNKNOWN` is offered as a real answer and is the default. A question nobody asked and a question
 * whose answer is no are different facts; a form that made them the same would tell everybody every
 * new lead had failed on all four.
 */

const TONE: Record<BantAnswer, 'neutral' | 'danger' | 'warning' | 'positive'> = {
  UNKNOWN: 'neutral',
  NO: 'danger',
  PARTIAL: 'warning',
  YES: 'positive',
};

export function QualificationPanel({
  id,
  bant,
  qualification,
}: {
  id: string;
  bant: Record<BantCriterion, BantAnswer>;
  qualification: Qualification;
}): React.JSX.Element {
  const client = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const answer = useMutation({
    mutationFn: (change: Partial<Record<BantCriterion, BantAnswer>>) =>
      updateRecord('leads', id, change),
    onSuccess: () => {
      setError(null);
      void client.invalidateQueries({ queryKey: ['record', 'leads', id] });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  return (
    <div className="stack">
      <div className="cell-inline">
        <span className="metric__value">{qualification.score}</span>
        <span className="muted small">
          {t('crm.bant.answered')
            .replace('{n}', String(qualification.answered))
            .replace('{total}', String(BANT_CRITERIA.length))}
        </span>

        {qualification.qualified && <Badge tone="positive">{t('crm.bant.qualified')}</Badge>}
      </div>

      {qualification.blockers.length > 0 && (
        <p className="muted small">
          {t('crm.bant.blockedBy')}:{' '}
          {qualification.blockers.map((each) => t(`crm.bant.${each}`)).join(', ')}
        </p>
      )}

      <div className="bant">
        {BANT_CRITERIA.map((criterion) => (
          <div key={criterion} className="bant__row">
            <span className="cell-stack">
              <span className="strong">{t(`crm.bant.${criterion}`)}</span>
              <span className="muted small">{t(`crm.bant.${criterion}.hint`)}</span>
            </span>

            <span className="bant__answers">
              {BANT_ANSWERS.map((option) => (
                <button
                  key={option}
                  type="button"
                  className={`chip${bant[criterion] === option ? ` chip--${TONE[option]}` : ''}`}
                  disabled={answer.isPending}
                  onClick={() => answer.mutate({ [criterion]: option })}
                >
                  {t(`crm.bant.answer.${option}`)}
                </button>
              ))}
            </span>
          </div>
        ))}
      </div>

      {error && (
        <p className="alert small" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
